import { SelectionState, EditorState, Modifier } from 'draft-js';
import { setSelectionToSibling, getSelectionCollapseInfo, getBlockMapKeys, removeBlock, insertBlock } from "lib/helpers";
import { Set } from "immutable";
import Debug from 'debug';
import { decorateFocusable } from "./decorateFocusable";
import { createKeyStore } from "./createKeyStore";

const debug = Debug('atomic-block');

const defaultOptions = {
	theme: {
		focused: 'atomic-focused',
		focusable: 'atomic-focusable',
	},

	/**
	 * 如果给了这个方法，编辑器里的 Atomic 支持懒加载
	 */
	findScrollParent: null,
}

const resolveFocusCommand = (command, editorState, evt) => {
	if (evt && evt.shiftKey) return null;
	const { isAtStartOfBlock, isAtEndOfBlock, block } = getSelectionCollapseInfo(editorState);
	if (command === 'arrow-up' || command === 'arrow-left' && isAtStartOfBlock) {
		return 'prev';
	}
	if (command === 'arrow-down' || command === 'arrow-right' && (isAtEndOfBlock || block && block.getType() === 'atomic')) {
		return 'next';
	}
};


/**
 * @returns {DraftEditorPlugin}
 */
export function createFocusPlugin(options = defaultOptions) {
	const { theme, findScrollParent } = { ...defaultOptions, ...options };

	/**
	 * 并不是每个 atomic block 都是 focusable，这里记录下被插件装饰过的 Atomic Block
	 */
	const keyStore = createKeyStore();
	const isFocusableAtomicBlock = block => (
		block && block.getType() === 'atomic' && keyStore.contains(block.getKey())
	);

	/**
	 * 记录上次的内容和选区，用于识别下一次操作是否强制重新渲染
	 */
	let lastContent;
	let lastSelection;

	return {
		name: 'focus',

		handleChange(editorState) {
			// 参考：https://github.com/draft-js-plugins/draft-js-plugins/blob/master/draft-js-focus-plugin/src/index.js#L40-L74

			// 内容如果已经改变了，那么 AtomicBlock 一定会被重新渲染
			const content = editorState.getCurrentContent();
			if (!content.equals(lastContent)) {
				lastContent = content;
				return editorState;
			}
			lastContent = content;

			// 如果选区没有发生变化，不需要重新渲染
			const selection = editorState.getSelection();
			if (lastSelection && selection.equals(lastSelection)) {
				lastSelection = selection;
				return editorState;
			}

			// 如果上一个选区包含 focusable atomic block，重新渲染
			if (lastSelection) {
				const lastBlockMapKeys = getBlockMapKeys(content, lastSelection);
				if (lastBlockMapKeys.some(key => keyStore.contains(key))) {
					lastSelection = selection;
					// 强制重新渲染
					return EditorState.forceSelection(editorState, selection);
				}
			}

			// 如果当前选区包含 focusable atomic block，也要重新渲染
			const currentBlockMapKeys = getBlockMapKeys(content, selection);
			if (currentBlockMapKeys.some(key => keyStore.contains(key))) {
				 lastSelection = selection;
				 // 强制重新渲染
				 return EditorState.forceSelection(editorState, selection);
			}

			return editorState;
		},

		handleCommand(store, command, evt) {
			const editorState = store.getEditorState();
			const content = editorState.getCurrentContent();
			const selection = editorState.getSelection();

			// 选区跨了多个 block 的情况不处理
			if (selection.getAnchorKey() !== selection.getFocusKey()) {
				 return 'not-handled';
			}

			const selectedBlockKey = selection.getAnchorKey();
			const selectedBlock = content.getBlockForKey(selectedBlockKey);
			const prevBlock = content.getBlockBefore(selectedBlockKey);
			const nextBlock = content.getBlockAfter(selectedBlockKey);

			const focusCommand = resolveFocusCommand(command, editorState, evt);

			// 当前选中了 atomic block，使用方向键切换
			if (focusCommand && isFocusableAtomicBlock(selectedBlock)) {
				debug('atomic focus to ' + focusCommand);
				const blockToFocus = focusCommand === 'prev' ? prevBlock : nextBlock;
				// 表格自己会处理
				if (blockToFocus && blockToFocus.getType() !== 'table') {
					return setSelectionToSibling(store, focusCommand, evt) ? 'handled' : 'not-handled';
				}
				if (focusCommand === 'next' && !nextBlock) {
					return store.setEditorState(insertBlock(editorState, selectedBlockKey, 'after')), 'handled';
				}
				return 'not-handled';
			}

			// 要移动的光标前面或后面是 atomic block
			if (focusCommand === 'next' && isFocusableAtomicBlock(nextBlock)
				|| focusCommand === 'prev' && isFocusableAtomicBlock(prevBlock)
			) {
				debug('atomic focus from ' + focusCommand);
				return setSelectionToSibling(store, focusCommand, evt) ? 'handled' : 'not-handled';
			}

			// 删除当前选中的
			if (command === 'backspace' || command === 'delete') {
				if (isFocusableAtomicBlock(selectedBlock)) {
					removeBlock(store, selectedBlock, command === 'delete' ? 'backward' : 'foward');
					return 'handled';
				}
			}

			return 'not-handled';
		},

		/**
		 * 返回支持 focusable 的 Atomic 插件
		 */
		decoratePlugin(plugin) {
			return decorateFocusable(plugin, { keyStore, theme, findScrollParent });
		}
	}
}



// WEBPACK FOOTER //
// ./lib/editor/DraftEditor/plugins/focusPlugin/createFocusPlugin.js